import { useState, useEffect, useCallback } from "react";
import { Check, X as XIcon, CreditCard, Loader2, ExternalLink, XCircle, Crown, Zap } from "lucide-react";
import { Button } from "../components/ui/button";
import { Navbar } from "../components/Navbar";
import { toast } from "sonner";
import api from "../lib/api";

const FEATURES = [
  { name: "Community Feed", desc: "View posts & discussions", free: "view", premium: true, super: true },
  { name: "Post & Comment", desc: "Create posts, comment, like", free: false, premium: true, super: true },
  { name: "Vita AI Plans", desc: "Personalized meal & fitness plans", free: false, premium: true, super: true },
  { name: "Challenges", desc: "Join & create fitness challenges", free: "view", premium: true, super: true },
  { name: "Groups", desc: "Create & join private groups", free: false, premium: true, super: true },
  { name: "Direct Messages", desc: "Chat with other members", free: false, premium: true, super: true },
  { name: "VitaMarket", desc: "Buy & sell fitness products", free: false, premium: true, super: true },
  { name: "Goal Tracking", desc: "Track daily health progress", free: false, premium: true, super: true },
  { name: "Community Badges", desc: "Earn badges from challenges", free: false, premium: true, super: true },
  { name: "Vita Coach", desc: "Personal AI fitness coach chat", free: false, premium: false, super: true },
  { name: "AI Performance Analysis", desc: "AI-powered progress insights", free: false, premium: false, super: true },
  { name: "Body Composition Analysis", desc: "BMI, body fat %, body type", free: false, premium: false, super: true },
  { name: "Smart Reminders", desc: "Automated health reminders", free: false, premium: false, super: true },
  { name: "Workout & Meal Visuals", desc: "Visual exercise & food guides", free: false, premium: false, super: true },
];

const FeatureCheck = ({ value }) => {
  if (value === true) return <Check className="h-5 w-5 text-[#0B6E4F]" />;
  if (value === "view") return <span className="text-xs text-slate-400 font-medium">View only</span>;
  return <XIcon className="h-4 w-4 text-slate-200" />;
};

export default function Pricing() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("vita_user");
    if (stored) setUser(JSON.parse(stored));
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      localStorage.setItem("vita_user", JSON.stringify(res.data));
      setUser(res.data);
    } catch (err) { console.error('Error:', err.message || err); }
  }, []);

  const handleSubscribe = async (planType) => {
    if (!user) { window.location.href = "/auth?mode=register"; return; }
    setLoading(planType);
    try {
      // Free trial creates a premium subscription with 7-day trial period
      const actualPlan = planType === "free_trial" ? "premium" : planType;
      const withTrial = planType === "free_trial";
      const res = await api.post("/paypal/create-subscription", {
        plan_type: actualPlan,
        with_trial: withTrial,
        return_url: `${window.location.origin}/payment-success?plan=${planType}`,
        cancel_url: `${window.location.origin}/pricing`
      });
      if (res.data.approve_url) window.location.href = res.data.approve_url;
      else toast.error("Failed to get PayPal approval URL");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create subscription"); }
    finally { setLoading(null); }
  };

  const handleCancel = async () => {
    if (!window.confirm("Are you sure? You'll lose access to premium features.")) return;
    setCancelling(true);
    try { await api.post("/paypal/cancel-subscription", { reason: "User requested" }); toast.success("Subscription cancelled"); await refreshUser(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to cancel"); }
    finally { setCancelling(false); }
  };

  const currentTier = user?.subscription_tier || "free";
  const isTier = (t) => currentTier === t;

  return (
    <div className="min-h-screen bg-transparent" data-testid="pricing-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#F2F9F6] rounded-full px-4 py-2 text-sm font-medium text-[#0B6E4F] mb-4">
            <CreditCard className="h-4 w-4" /> Subscription Plans
          </div>
          <h1 className="font-['Outfit'] text-4xl md:text-5xl font-bold tracking-tight text-[#0F172A] mb-3">Invest in Your Health</h1>
          <p className="text-base text-slate-500 max-w-xl mx-auto">Choose the plan that fits your goals. Cancel anytime.</p>
        </div>

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16">
          {/* Free Trial */}
          <div className={`border rounded-2xl p-7 transition-all ${(isTier("free_trial") || isTier("pending_trial")) ? "border-[#0B6E4F] bg-[#F2F9F6]" : "border-slate-200 bg-white"} shadow-sm`} data-testid="pricing-free">
            {isTier("free_trial") && <div className="text-xs font-bold text-[#0B6E4F] mb-2 uppercase tracking-wide">Current Plan</div>}
            {isTier("pending_trial") && <div className="text-xs font-bold text-amber-500 mb-2 uppercase tracking-wide">Add Payment to Start</div>}
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Free Trial</p>
            <div className="mt-3 mb-5">
              <span className="font-['Outfit'] text-5xl font-bold text-[#0F172A]">$0</span>
              <span className="text-slate-400 text-sm ml-2">/7 days</span>
            </div>
            <p className="text-sm text-slate-500 mb-6">Explore the platform with limited access. Payment method required to start your free trial.</p>
            <Button onClick={() => handleSubscribe("free_trial")} disabled={loading === "free_trial"} data-testid="start-trial-btn" className="w-full h-11 bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full font-semibold">
              {isTier("pending_trial") ? (loading === "free_trial" ? "Processing..." : "Start Free Trial") : isTier("free_trial") ? "Trial Active" : "Trial Expired"}
            </Button>
          </div>

          {/* Premium */}
          <div className={`rounded-2xl p-7 shadow-xl relative ${isTier("premium") ? "bg-[#095A40]" : "bg-[#0B6E4F]"} text-white ring-2 ring-[#0B6E4F] ring-offset-4`} data-testid="pricing-premium">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4F4DD] text-[#0B6E4F] text-xs font-bold px-4 py-1 rounded-full flex items-center gap-1">
              <Zap className="h-3 w-3" />{isTier("premium") ? "CURRENT PLAN" : "MOST POPULAR"}
            </div>
            <p className="text-sm font-medium text-[#D4F4DD] uppercase tracking-wide">Premium</p>
            <div className="mt-3 mb-5">
              <span className="font-['Outfit'] text-5xl font-bold">$7</span>
              <span className="text-white/60 text-sm ml-2">/month</span>
            </div>
            <p className="text-sm text-white/70 mb-6">Full access to community, AI plans, challenges, groups, messaging, and marketplace.</p>
            {isTier("premium") ? (
              <div className="space-y-2">
                <Button disabled className="w-full bg-white/20 text-white rounded-full font-semibold cursor-default">Active</Button>
                <Button onClick={handleCancel} disabled={cancelling} variant="ghost" className="w-full text-white/60 hover:text-white text-xs" data-testid="cancel-premium-btn">
                  {cancelling ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <XCircle className="h-3 w-3 mr-1" />} Cancel
                </Button>
              </div>
            ) : (
              <Button onClick={() => handleSubscribe("premium")} disabled={loading === "premium"} data-testid="subscribe-premium-btn" className="w-full bg-white text-[#0B6E4F] hover:bg-[#D4F4DD] rounded-full font-semibold shadow-lg">
                {loading === "premium" ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Processing...</> : !user ? "Sign Up" : <>Subscribe $7/mo <ExternalLink className="h-3 w-3 ml-2" /></>}
              </Button>
            )}
          </div>

          {/* Super Premium */}
          <div className={`border rounded-2xl p-7 transition-all relative ${isTier("super_premium") ? "border-[#1B3A5C] bg-blue-50" : "border-slate-200 bg-white"} shadow-sm`} data-testid="pricing-super">
            {isTier("super_premium") && <div className="text-xs font-bold text-[#1B3A5C] mb-2 uppercase tracking-wide">Current Plan</div>}
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-medium text-[#1B3A5C] uppercase tracking-wide">Super Premium</p>
              <Crown className="h-4 w-4 text-amber-500" />
            </div>
            <div className="mt-3 mb-5">
              <span className="font-['Outfit'] text-5xl font-bold text-[#0F172A]">$16</span>
              <span className="text-slate-400 text-sm ml-2">/month</span>
            </div>
            <p className="text-sm text-slate-500 mb-6">Everything in Premium plus AI Coach, body analysis, smart reminders, and visual guides.</p>
            {isTier("super_premium") ? (
              <div className="space-y-2">
                <Button disabled className="w-full bg-[#1B3A5C]/20 text-[#1B3A5C] rounded-full font-semibold cursor-default">Active</Button>
                <Button onClick={handleCancel} disabled={cancelling} variant="ghost" className="w-full text-slate-400 hover:text-slate-600 text-xs" data-testid="cancel-super-btn">
                  {cancelling ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <XCircle className="h-3 w-3 mr-1" />} Cancel
                </Button>
              </div>
            ) : (
              <Button onClick={() => handleSubscribe("super_premium")} disabled={loading === "super_premium"} data-testid="subscribe-super-btn" className="w-full bg-[#1B3A5C] hover:bg-[#152d48] text-white rounded-full font-semibold shadow-lg">
                {loading === "super_premium" ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Processing...</> : !user ? "Sign Up" : <>Subscribe $16/mo <ExternalLink className="h-3 w-3 ml-2" /></>}
              </Button>
            )}
          </div>
        </div>

        {/* Feature Comparison Table */}
        <div className="max-w-5xl mx-auto">
          <h2 className="font-['Outfit'] text-2xl font-bold text-[#0F172A] text-center mb-8">Feature Comparison</h2>
          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
            <div className="grid grid-cols-4 border-b border-slate-100 bg-slate-50 px-6 py-4">
              <div className="text-sm font-semibold text-slate-700">Feature</div>
              <div className="text-sm font-semibold text-slate-500 text-center">Free Trial</div>
              <div className="text-sm font-semibold text-[#0B6E4F] text-center">Premium</div>
              <div className="text-sm font-semibold text-[#1B3A5C] text-center">Super Premium</div>
            </div>
            {FEATURES.map((f, i) => (
              <div key={i} className={`grid grid-cols-4 px-6 py-3.5 items-center ${i % 2 === 0 ? "bg-white" : "bg-slate-50/50"} ${i < FEATURES.length - 1 ? "border-b border-slate-50" : ""}`} data-testid={`feature-row-${i}`}>
                <div>
                  <p className="text-sm font-medium text-[#0F172A]">{f.name}</p>
                  <p className="text-xs text-slate-400">{f.desc}</p>
                </div>
                <div className="flex justify-center"><FeatureCheck value={f.free} /></div>
                <div className="flex justify-center"><FeatureCheck value={f.premium} /></div>
                <div className="flex justify-center"><FeatureCheck value={f.super} /></div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-12">
          <p className="text-sm text-slate-500">Secure recurring payments via PayPal. Auto-billed monthly. Cancel anytime.</p>
        </div>
      </div>
    </div>
  );
}
